import { useEffect, useState, useCallback } from 'react';
import { useSession } from '@/Recoil/Session/Session';
import { useRoles } from '@/Recoil/Topik/Roles';
import { useExams } from '@/Recoil/Topik/Exams';
import { getQuestionnaire } from '@/Data/Topik';
import { navigateTo } from '@/Utils/routing';
import { QuestionnaireResponse } from '@/Data/Topik/types';
import './index.scss';

type TopikExamDetailPageProps = {
    examid: string;
};

const ROLE_ORDER: Record<string, number> = {
    admin:    4,
    manager:  3,
    lecturer: 1,
    student:  1,
};

const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}시간 ${minutes}분`;
};

function TopikExamDetailPage({ examid }: TopikExamDetailPageProps) {
    const { session } = useSession();
    const { roles, syncUserRole } = useRoles();
    const { getExam, syncExam, allocateExam } = useExams();

    const [examContent, setExamContent] = useState<QuestionnaireResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [allocateModalOpen, setAllocateModalOpen] = useState(false);
    const [allocateEmail, setAllocateEmail] = useState('');
    const [allocating, setAllocating] = useState(false);

    const userRole = roles[session.email];
    const roleLevel = ROLE_ORDER[userRole?.role] ?? 0;
    const cachedExam = getExam(examid);

    useEffect(() => {
        if (!session.email) {
            navigateTo('/login?redirectTo=/topik/exam/' + examid);
            return;
        }
        syncUserRole({ email: session.email });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session.email]);

    useEffect(() => {
        if (roleLevel >= ROLE_ORDER['manager']) {
            fetchExamContent();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roleLevel, examid]);

    const fetchExamContent = async () => {
        setLoading(true);
        setError(null);

        try {
            // Sync exam stats
            await syncExam({ examid });

            // Get full exam content
            const content = await getQuestionnaire({ examid });
            if (content) {
                setExamContent(content);
            } else {
                setError('시험을 불러올 수 없습니다.');
            }
        } catch (e) {
            setError('시험을 불러오는 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const openAllocateModal = useCallback(() => {
        setAllocateEmail('');
        setAllocateModalOpen(true);
    }, []);

    const closeAllocateModal = useCallback(() => {
        setAllocateModalOpen(false);
        setAllocateEmail('');
    }, []);

    const handleAllocate = useCallback(async () => {
        if (!allocateEmail.trim()) { return; }

        setAllocating(true);
        try {
            const result = await allocateExam({
                examid,
                userid: allocateEmail.trim()
            });

            if (result) {
                alert(`시험이 할당되었습니다.\nQuestionnaire ID: ${result.questionnaireid}`);
                closeAllocateModal();
            } else {
                alert('시험 할당에 실패했습니다.');
            }
        } finally {
            setAllocating(false);
        }
    }, [examid, allocateEmail, allocateExam, closeAllocateModal]);

    if (roleLevel < ROLE_ORDER['manager']) {
        return (
            <div className="TopikExamDetailPage">
                <div className="Container">
                    <div className="Breadcrumb">
                        <button onClick={() => navigateTo('/topik/exam')}>시험 목록</button>
                        <span>{">"}</span>
                        <span>{examid}</span>
                    </div>
                    <div className="NoPermission">권한이 없습니다.</div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="TopikExamDetailPage">
                <div className="Container">
                    <div className="Breadcrumb">
                        <button onClick={() => navigateTo('/topik/exam')}>시험 목록</button>
                        <span>{">"}</span>
                        <span>{examid}</span>
                    </div>
                    <div className="Loading">로딩 중...</div>
                </div>
            </div>
        );
    }

    if (error || !examContent) {
        return (
            <div className="TopikExamDetailPage">
                <div className="Container">
                    <div className="Breadcrumb">
                        <button onClick={() => navigateTo('/topik/exam')}>시험 목록</button>
                        <span>{">"}</span>
                        <span>{examid}</span>
                    </div>
                    <div className="Error">{error || '시험을 찾을 수 없습니다.'}</div>
                </div>
            </div>
        );
    }

    const { exam, metadata } = examContent;

    return (
        <div className="TopikExamDetailPage">
            <div className="Container">
                <div className="Breadcrumb">
                    <button onClick={() => navigateTo('/topik/exam')}>시험 목록</button>
                    <span>{">"}</span>
                    <span>{exam.title}</span>
                </div>

                <div className="ExamHeader">
                    <h1 className="ExamTitle">{exam.title}</h1>
                    <button className="AllocateButton" onClick={openAllocateModal}>시험 할당</button>
                </div>

                <div className="ExamInfo">
                    <div className="InfoRow">
                        <span className="LevelBadge">{exam.topik_level}</span>
                        <span className="TargetBadge">{`Level ${exam.target_level}`}</span>
                    </div>
                    <div className="InfoRow">
                        <span className="InfoItem">{`시간: ${formatDuration(exam.total_time)}`}</span>
                        <span className="InfoItem">{`총점: ${exam.total_points}점`}</span>
                        <span className="InfoItem">{`합격점: ${exam.pass_score}점`}</span>
                    </div>
                    {exam.description && (
                        <p className="Description">{exam.description}</p>
                    )}
                </div>

                {cachedExam?.statistics && (
                    <div className="Statistics">
                        <h2 className="SectionTitle">통계</h2>
                        <div className="StatGrid">
                            <div className="StatItem">
                                <span className="StatValue">{cachedExam.statistics.total_allocations}</span>
                                <span className="StatLabel">총 할당</span>
                            </div>
                            <div className="StatItem">
                                <span className="StatValue">{cachedExam.statistics.active_allocations}</span>
                                <span className="StatLabel">진행중</span>
                            </div>
                            <div className="StatItem">
                                <span className="StatValue">{cachedExam.statistics.completed_attempts}</span>
                                <span className="StatLabel">완료</span>
                            </div>
                            <div className="StatItem">
                                <span className="StatValue">{cachedExam.statistics.average_score?.toFixed(1) ?? '-'}</span>
                                <span className="StatLabel">평균점수</span>
                            </div>
                        </div>
                    </div>
                )}

                <div className="SectionBreakdown">
                    <h2 className="SectionTitle">섹션 구성</h2>
                    <div className="SectionGrid">
                        <div className="SectionCard clickable" onClick={() => navigateTo(`/topik/exam/${examid}/listening`)}>
                            <h3>Listening</h3>
                            <p>{`${metadata.sectionBreakdown.listening.questions}문제`}</p>
                            <p>{`${metadata.sectionBreakdown.listening.points}점`}</p>
                            <p>{`${metadata.sectionBreakdown.listening.groups}그룹`}</p>
                        </div>
                        <div className="SectionCard clickable" onClick={() => navigateTo(`/topik/exam/${examid}/reading`)}>
                            <h3>Reading</h3>
                            <p>{`${metadata.sectionBreakdown.reading.questions}문제`}</p>
                            <p>{`${metadata.sectionBreakdown.reading.points}점`}</p>
                            <p>{`${metadata.sectionBreakdown.reading.groups}그룹`}</p>
                        </div>
                        <div className="SectionCard clickable" onClick={() => navigateTo(`/topik/exam/${examid}/writing`)}>
                            <h3>Writing</h3>
                            <p>{`${metadata.sectionBreakdown.writing.questions}문제`}</p>
                            <p>{`${metadata.sectionBreakdown.writing.points}점`}</p>
                            <p>{`${metadata.sectionBreakdown.writing.groups}그룹`}</p>
                        </div>
                    </div>
                    <p className="TotalQuestions">{`총 ${metadata.totalQuestions}문제`}</p>
                </div>
            </div>

            {allocateModalOpen && (
                <>
                    <div className="ModalOverlay active" onClick={closeAllocateModal}></div>
                    <div className="AllocateModal Modal active" onClick={(e) => e.stopPropagation()}>
                        <div className="ModalContent">
                            <div className="ModalHeader">
                                <h2 className="ModalTitle">시험 할당</h2>
                            </div>
                            <div className="ModalBody">
                                <p className="ModalDescription">{`"${exam.title}" 시험을 할당할 사용자 정보를 입력하세요.`}</p>
                                <div className="InputGroup">
                                    <label className="InputLabel">사용자 ID 또는 이메일</label>
                                    <input
                                        type="text"
                                        className="Input"
                                        placeholder="userid 또는 email"
                                        value={allocateEmail}
                                        onChange={(e) => setAllocateEmail(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="ModalFooter">
                                <button className="ModalButton TertiaryButton" onClick={closeAllocateModal}>취소</button>
                                <button
                                    className="ModalButton PrimaryButton"
                                    onClick={handleAllocate}
                                    disabled={allocating || !allocateEmail.trim()}
                                >
                                    {allocating ? '할당 중...' : '할당하기'}
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default TopikExamDetailPage;
